package meineglasfaser.frontend.admin.pages.customer

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.navigation.Anchor
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import meineglasfaser.api.*
import meineglasfaser.api.routes.AdminRoutes
import meineglasfaser.api.routes.CustomerPortalRoutes.UpdateData.Invoicing
import meineglasfaser.api.ubl.UBLPostalAddress
import meineglasfaser.frontend.admin.components.widgets.YaTable
import meineglasfaser.frontend.admin.components.widgets.named
import meineglasfaser.frontend.admin.components.widgets.sortedAsc
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.*
import meineglasfaser.frontend.common.components.yaform.*
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*

@Page
@Composable
fun CustomerListPage(ctx: PageContext) {
    val customerNid = ctx.route.fragment?.toIntOrNull()

    YaTable(
        header = { H1 { Text("Kundenstamm") } },
        idProperty = CustomerPaginated::id,
        columns = listOf(
            CustomerPaginated::nid named "Kd.Nr.",
            CustomerPaginated::type named "Typ",
            CustomerPaginated::active named "Aktiv?",
            CustomerPaginated::name named "Name",
            CustomerPaginated::invoicingAddress named "Rechnungsadresse",
            CustomerPaginated::companyVatNumber named "UID-Nummer",
            CustomerPaginated::email named "E-Mail",
            CustomerPaginated::telFixedLine named "Festnetz",
            CustomerPaginated::telMobile named "Mobil",
        ),
        initialOptions = YaTableOptions.DEFAULT.copy(
            orderBy = listOf(
                CustomerPaginated::nid.sortedAsc(),
            ),
        ),
        fetch = { options ->
            BackendApi.post(AdminRoutes.Customers()) {
                withAuth()
                setJsonBody(options)
            }
        },
        onViewEntry = { entry ->
            ctx.router.navigateTo("#${entry.nid}")
        },
    )

    if (customerNid != null) {
        CustomerModalWrapper(
            customerNid = customerNid,
            onHide = {
                ctx.router.navigateTo("#")
            },
        )
    }
}

@Composable
private fun CustomerModalWrapper(
    customerNid: Int,
    onHide: () -> Unit,
) {
    var version by remember { mutableIntStateOf(0) }
    val customerDetailed = rememberQuery<CustomerDetailed?>(null, customerNid, version) {
        BackendApi.get(AdminRoutes.Customers.ByNid(customerNid)) {
            withAuth()
        }
    }
    if (customerDetailed != null) {
        CustomerModal(
            customerDetailed = customerDetailed,
            onHide = onHide,
            onUpdate = { version++ },
        )
    }
}

@Composable
private fun CustomerModal(
    customerDetailed: CustomerDetailed,
    onHide: () -> Unit,
    onUpdate: () -> Unit,
) {
    val (customer, orders) = customerDetailed
    Modal(onHide, size = ModalSize.FULLSCREEN) {
        Header {
            ModalCloseBtn(onHide)
            Text("Stammblatt für Kunde #${customer.nid}")
        }
        Div({ classes("row-fluid", "flex-grow")}) {
            Div({ classes("col-12", "col-lg-4") }) {
                CustomerAccountDataForm(
                    customer = customer,
                    onUpdate = onUpdate,
                )
                CustomerPhoneNumberCard(
                    customer = customer,
                    onUpdate = onUpdate,
                )
            }
            Div({ classes("col-12", "col-lg-4") }) {
                CustomerInvoicingAddressCard(
                    customer = customer,
                    onUpdate = onUpdate,
                )
                CustomerInvoicingDataCard(
                    customer = customer,
                    onUpdate = onUpdate,
                )
            }
            Div({ classes("col-12", "col-lg-4") }) {
                CustomerOrdersList(orders)
            }
        }
    }
}

@Composable
private fun CustomerAccountDataForm(
    customer: Customer,
    onUpdate: () -> Unit,
) {
    var isEditing by remember { mutableStateOf(false) }
    YaForm(
        initial = customer,
        onSubmitScope = { data ->
            val body = customer.copy(
                active = data.boolean(Customer::active),
                accountEmail = data.string(Customer::accountEmail),
                individualFirstName = customer.individualFirstName,
                individualLastName = customer.individualLastName,
                individualDob = customer.individualDob,
                companyName = customer.companyName,
                companyVatNumber = customer.companyVatNumber,
                companyContactPerson = customer.companyContactPerson,
            )
            BackendApi.post(AdminRoutes.Customers.ByNid(customer.nid)) {
                withAuth()
                setJsonBody(body)
            }
            isEditing = false
            onUpdate()
        }
    ) {
        val selectedType = state.data[Customer::type.name] ?: customer.type.name
        Article {
            Header { Text("Account Daten") }
            Div({ classes("row-fluid") }) {
                Div({ classes("col-12", "col-sm-7") }) {
                    YaFormNumberInput(
                        prop = Customer::nid,
                        label = { Text("Kundennummer") },
                        readonly = true,
                    )
                }
                Div({ classes("col-12", "col-sm-5") }) {
                    YaFormTextInput(
                        prop = Customer::accountServicePIN,
                        label = { Text("Service PIN") },
                        type = InputType.Text,
                        readonly = true,
                    )
                }
            }
            YaFormCheckInput(
                prop = Customer::active,
                label = { Text("Aktiv") },
                disabled = !isEditing,
            )
            YaFormTextInput(
                prop = Customer::accountEmail,
                label = { Text("Account E-Mail") },
                type = InputType.Email,
                required = true,
                readonly = !isEditing,
            )
            YaFormDropdown(
                prop = Customer::type,
                label = { Text("Kundentyp") },
                options = CustomerType.entries.map { it to it.name },
                required = true,
                disabled = true,
            )
            when (selectedType) {
                CustomerType.INDIVIDUAL.name -> {
                    YaFormTextInput(
                        prop = Customer::individualFirstName,
                        label = { Text("Vorname") },
                        type = InputType.Text,
                        required = true,
                        readonly = !isEditing,
                    )
                    YaFormTextInput(
                        prop = Customer::individualLastName,
                        label = { Text("Nachname") },
                        type = InputType.Text,
                        required = true,
                        readonly = !isEditing,
                    )
                    YaFormLocalDateInput(
                        prop = Customer::individualDob,
                        label = { Text("Geburtsdatum") },
                        required = false,
                        disabled = !isEditing,
                    )
                }
                CustomerType.COMPANY.name -> {
                    YaFormTextInput(
                        prop = Customer::companyName,
                        label = { Text("Firmenname") },
                        type = InputType.Text,
                        required = true,
                        readonly = !isEditing,
                    )
                    YaFormTextInput(
                        prop = Customer::companyVatNumber,
                        label = { Text("UID-Nummer") },
                        type = InputType.Text,
                        required = false,
                        readonly = !isEditing,
                    )
                    YaFormTextInput(
                        prop = Customer::companyContactPerson,
                        label = { Text("Ansprechpartner") },
                        type = InputType.Text,
                        required = false,
                        readonly = !isEditing,
                    )
                }
                else -> {
                    Text("Unbekannter Kundentyp oder nicht ausgewählt: $selectedType")
                }
            }
        }
    }
}

@Composable
private fun CustomerPhoneNumberCard(
    customer: Customer,
    onUpdate: () -> Unit,
) {
    YaForm(
        initial = AdminRoutes.Customers.ByNid.UpdatePhoneNumbers.RequestBody(
            telFixedLine = customer.telFixedLine ?: "",
            telMobile = customer.telMobile ?: "",
        ),
        onSubmitScope = { data ->
            val body = AdminRoutes.Customers.ByNid.UpdatePhoneNumbers.RequestBody(
                telFixedLine = data.string(AdminRoutes.Customers.ByNid.UpdatePhoneNumbers.RequestBody::telFixedLine),
                telMobile = data.string(AdminRoutes.Customers.ByNid.UpdatePhoneNumbers.RequestBody::telMobile),
            )
            BackendApi.post(AdminRoutes.Customers.ByNid.UpdatePhoneNumbers(AdminRoutes.Customers.ByNid(customer.nid))) {
                withAuth()
                setJsonBody(body)
            }.fold(
                onSuccess = {
                    Toasts.display(Toast.success("Telefonnummern erfolgreich aktualisiert."))
                    onUpdate()
                },
                onFailure = { error ->
                    Toasts.display(Toast.error("Fehler beim Aktualisieren der Telefonnummern: ${error.message}"))
                }
            )
        }
    ) {
        Article {
            Header { Text("Telefonnummern") }
            YaFormTextInput(
                prop = AdminRoutes.Customers.ByNid.UpdatePhoneNumbers.RequestBody::telFixedLine,
                label = { Text("Festnetz") },
                type = InputType.Tel,
                required = false,
            )
            YaFormTextInput(
                prop = AdminRoutes.Customers.ByNid.UpdatePhoneNumbers.RequestBody::telMobile,
                label = { Text("Mobil") },
                type = InputType.Tel,
                required = false,
            )
            YaFormButton(ButtonType.Submit) { Text("Speichern") }
        }
    }
}

@Composable
private fun CustomerInvoicingAddressCard(
    customer: Customer,
    onUpdate: () -> Unit,
) {
    YaForm(
        initial = customer.invoicingAddress ?: UBLPostalAddress.EMPTY_AUSTRIA,
        onSubmitScope = { data ->
            val address = UBLPostalAddress(
                streetName = data.string(UBLPostalAddress::streetName),
                cityName = data.string(UBLPostalAddress::cityName),
                postalCode = data.string(UBLPostalAddress::postalCode),
                countryCode = data.string(UBLPostalAddress::countryCode),
            )
            BackendApi.post(AdminRoutes.Customers.ByNid.UpdateInvoicingAddress(AdminRoutes.Customers.ByNid(customer.nid))) {
                withAuth()
                setJsonBody(address)
            }.fold(
                onSuccess = {
                    Toasts.display(Toast.success("Rechnungsadresse erfolgreich aktualisiert."))
                    onUpdate()
                },
                onFailure = { error ->
                    Toasts.display(Toast.error("Fehler beim Aktualisieren der Rechnungsadresse: ${error.message}"))
                }
            )
        }
    ) {
        Article {
            Header { Text("Rechnungsadresse") }
            UBLPostalAddressInputs(
                streetName = UBLPostalAddress::streetName,
                cityName = UBLPostalAddress::cityName,
                postalZone = UBLPostalAddress::postalCode,
                countryCode = UBLPostalAddress::countryCode,
            )
            YaFormButton(ButtonType.Submit) { Text("Speichern") }
        }
    }
}

@Composable
private fun CustomerInvoicingDataCard(
    customer: Customer,
    onUpdate: () -> Unit,
) {
    YaForm(
        initial = AdminRoutes.Customers.ByNid.UpdateInvoicingData.RequestBody(
            invoicingEmail = customer.invoicingEmail ?: "",
            invoicingDeliveryMode = customer.invoicingDeliveryMode,
            invoicingAccountHolder = customer.invoicingAccountHolder ?: "",
            invoicingIBAN = customer.invoicingIBAN ?: "",
        ),
        onSubmitScope = { data ->
            val body = Invoicing.RequestBody(
                invoicingEmail = data.string(AdminRoutes.Customers.ByNid.UpdateInvoicingData.RequestBody::invoicingEmail),
                invoicingDeliveryMode = InvoiceDeliveryMode.valueOf(
                    data.string(AdminRoutes.Customers.ByNid.UpdateInvoicingData.RequestBody::invoicingDeliveryMode)
                ),
                invoicingAccountHolder = data.string(AdminRoutes.Customers.ByNid.UpdateInvoicingData.RequestBody::invoicingAccountHolder),
                invoicingIBAN = data.string(AdminRoutes.Customers.ByNid.UpdateInvoicingData.RequestBody::invoicingIBAN),
            )
            BackendApi.post(AdminRoutes.Customers.ByNid.UpdateInvoicingData(AdminRoutes.Customers.ByNid(customer.nid))) {
                withAuth()
                setJsonBody(body)
            }.fold(
                onSuccess = {
                    Toasts.display(Toast.success("Rechnungsdaten erfolgreich aktualisiert."))
                    onUpdate()
                },
                onFailure = { error ->
                    Toasts.display(Toast.error("Fehler beim Aktualisieren der Rechnungsdaten: ${error.message}"))
                }
            )
        }
    ) {
        Article {
            Header { Text("Rechnungsdaten") }
            YaFormTextInput(
                prop = AdminRoutes.Customers.ByNid.UpdateInvoicingData.RequestBody::invoicingEmail,
                label = { Text("Rechnungs-E-Mail") },
                type = InputType.Email,
                required = true,
            )
            YaFormDropdown(
                prop = AdminRoutes.Customers.ByNid.UpdateInvoicingData.RequestBody::invoicingDeliveryMode,
                label = { Text("Rechnungszustellung") },
                options = listOf(
                    InvoiceDeliveryMode.EMAIL to "Per E-Mail (PDF)",
                    InvoiceDeliveryMode.POSTAL to "Per Post (Papier)",
                ),
                required = true,
                disabled = true,
            )
            YaFormTextInput(
                prop = AdminRoutes.Customers.ByNid.UpdateInvoicingData.RequestBody::invoicingAccountHolder,
                label = { Text("Kontoinhaber") },
                type = InputType.Text,
                required = true,
            )
            YaFormTextInput(
                prop = AdminRoutes.Customers.ByNid.UpdateInvoicingData.RequestBody::invoicingIBAN,
                label = { Text("IBAN") },
                type = InputType.Text,
                pattern = "AT[0-9]{2}[0-9]{16}",
                required = true,
            )
            YaFormButton(ButtonType.Submit) { Text("Speichern") }
        }
    }
}

@Composable
private fun CustomerOrdersList(
    orders: List<OrderWithEntries>,
) {
    Article {
        Header { Text("Verlinkte Bestellungen") }
        if (orders.isEmpty()) {
            Text("Keine Bestellungen gefunden.")
        } else {
            orders.forEachIndexed { index, (order, entries) ->
                Div {
                    if (index > 0) {
                        Hr()
                    }
                    Anchor("/shop/bestellungen/#${order.nid}", openInternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB) {
                        B { Text("Bestellung #${order.nid}") }
                    }
                    Div({ classes("row-fluid") }) {
                        Div({ classes("col-12", "col-md-7") }) {
                            entries.forEach { entry ->
                                Div({
                                    if (entry.isAddon) {
                                        classes("addon-offset")
                                    }
                                }) {
                                    Text("${entry.quantity}x ${entry.product.title}")
                                }
                            }
                        }
                        Div({ classes("col-12", "col-md-5") }) {
                            UBLPostalAddressView(
                                address = order.deliveryAddress,
                                vertical = true,
                                showViewOnMapsLink = true,
                            )
                        }
                    }
                }
            }
        }
    }
}
